import {
  CmpEventRegistrationFieldsFragment, CmpEventSponsorshipRequestFieldsFragment,
  CmpEventRegistrationStatusEnum, CmpEventSponsorshipRequestStatusEnum,
} from '../../../../graphql/types';
import { getActiveEventAttendees, getEventRegistrationTicketCount } from './eventSalesAttendeeUpdates';
import { EventTicketTypeNameSource, getEventRegistrationTicketTypeLines } from './eventAttendeeTicketGroups';

/** One line of the Ticket Type cell. Sponsorship rows show no quantity. */
export type EventTicketSalesTypeLine = Readonly<{ label: string; quantity: number | null }>;

export const getEventTicketSalesRows = ({ registrations, sponsorships, ticketTypes = [] }: {
  registrations: readonly CmpEventRegistrationFieldsFragment[];
  sponsorships: readonly CmpEventSponsorshipRequestFieldsFragment[];
  ticketTypes?: readonly EventTicketTypeNameSource[];
}) => [
  ...registrations.filter(source => source.status !== CmpEventRegistrationStatusEnum.Canceled).map(source => ({
    key: `registration:${source.id}`, kind: 'registration' as const, registration: source, sponsorship: null,
    source, attendees: getActiveEventAttendees(source.attendees), count: getEventRegistrationTicketCount(source),
    company: source.selectedMemberCompanyNameSnapshot || source.billingSnapshot?.customerName || '',
    ticketTypeLines: getEventRegistrationTicketTypeLines({ registration: source, ticketTypes })
      .map(({ label, quantity }): EventTicketSalesTypeLine => ({ label, quantity })),
  })),
  ...sponsorships.filter(source => [CmpEventSponsorshipRequestStatusEnum.Live,
    CmpEventSponsorshipRequestStatusEnum.ApprovedAwaitingPayment, CmpEventSponsorshipRequestStatusEnum.PaymentPending,
    CmpEventSponsorshipRequestStatusEnum.PaymentActionRequired].includes(source.status)).map(source => {
    const attendees = getActiveEventAttendees([...source.includedAttendees, ...(source.additionalAttendees || [])]);
    return {
      key: `sponsorship:${source.id}`, kind: 'sponsorship' as const, registration: null, sponsorship: source,
      source, attendees, count: attendees.length,
      company: source.selectedMemberCompanyNameSnapshot || source.sponsorDisplayName || source.billingSnapshot?.customerName || '',
      // A sponsorship row shows one line with the included ticket type(s) and no quantity (design D:13553).
      ticketTypeLines: [{ label: Array.from(new Set(attendees.map(attendee => attendee.ticketTypeNameSnapshot))).join(', '), quantity: null }] as EventTicketSalesTypeLine[],
    };
  }).filter(row => row.count > 0),
].map(row => ({ ...row,
  attendeeName: row.attendees.map(attendee => [attendee.firstName, attendee.lastName].filter(Boolean).join(' ')).join(', '),
  companyName: Array.from(new Set(row.attendees.map(attendee => attendee.companyName?.trim() || row.company).filter(Boolean))).join(', ') || row.company,
  /** Sort key: the first line label (design sorts the Ticket Type column by it). */
  ticketType: row.ticketTypeLines[0]?.label || '',
}));
